import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { PixiSettings } from "../../../pixiSettings";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";

// Background animation configuration
const BACKGROUND_ANIMATION_CONFIG = {
  // Vertical offset for proper alignment (positive = down, negative = up)
  // Only applied on desktop and landscape orientations
  yOffset: 220, // Normal offset
  yOffsetIntro: 420, // Bigger offset when intro screen is active

  // Horizontal offset for spine animation only (positive = right, negative = left)
  // Applied consistently across all devices/orientations
  xOffset: -7, // Adjust this value to shift spine right (e.g., 10 for 10px right)

  // Additional vertical offset for spine animation only (positive = down, negative = up)
  // This is ADDED to the shared yOffset/yOffsetIntro
  // Applied consistently across all devices/orientations
  yOffsetSpine: 0, // Additional Y offset for spine only (e.g., 5 for 5px down)
};

// Manages background spine animations
class BackgroundAnimationController {
  constructor(
    container,
    backgroundSprite,
    dimensions,
    symbolWidth,
    symbolScale,
    device,
    orientation,
    introPopupActive = false
  ) {
    this.container = container;
    this.backgroundSprite = backgroundSprite;
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolScale = symbolScale;
    this.device = device;
    this.orientation = orientation;
    this.introPopupActive = introPopupActive;
    this.backgroundSpine = null;
    this.updateYOffset();
    this.initializeBackground();
  }

  // Initialize background spine animation
  initializeBackground() {
    this.backgroundSpine = this.createBackgroundSpine();

    if (!this.backgroundSpine) {
      return;
    }

    // Simple validation
    if (!this.backgroundSpine.skeleton || !this.backgroundSpine.state) {
      return;
    }

    // Setup spine
    this.backgroundSpine.position.set(0, 0);
    this.backgroundSpine.zIndex = 1;
    this.backgroundSpine.visible = true;

    // Add to container
    this.container.addChild(this.backgroundSpine);

    // Setup event listener for drop sounds
    this.setupDropSoundListener();

    // Apply responsive scaling and positioning
    this.applyResponsiveBackgroundScaling();
  }

  /**
   * Setup event listener for drop sounds from spine animation events
   * Plays a random drop sound (drop_1 through drop_12) when drop event fires
   */
  setupDropSoundListener() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    // Listen for events from the spine animation
    this.backgroundSpine.state.addListener({
      event: (entry, event) => {
        // Check if the event name contains "drop" (case insensitive)
        if (event && event.data && event.data.name) {
          const eventName = event.data.name.toLowerCase();
          if (eventName.includes("drop") || eventName === "water_drop") {
            this.playRandomDropSound();
          }
        }
      },
    });
  }

  /**
   * Plays a random drop sound from drop_1 to drop_12
   */
  playRandomDropSound() {
    // Generate random number between 1 and 12
    const randomDropNumber = Math.floor(Math.random() * 12) + 1;
    const dropSoundName = `drop_${randomDropNumber}`;

    // Use soundManager to respect sound settings (including background music requirement)
    soundManager.play(SOUND_NAME[dropSoundName]);
  }

  // Create background spine instance
  createBackgroundSpine() {
    try {
      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "background_animation-skeleton";
      const atlasAlias = "background_animation-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      // Create independent spine instance
      const backgroundSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      // Verify the spine was created properly
      if (
        !backgroundSpine ||
        !backgroundSpine.skeleton ||
        !backgroundSpine.state
      ) {
        return null;
      }

      // Verify skeleton has required data
      if (
        !backgroundSpine.skeleton.data ||
        !backgroundSpine.skeleton.data.animations
      ) {
        return null;
      }

      return backgroundSpine;
    } catch (error) {
      return null;
    }
  }

  // Play base_idle animation (for base game)
  playBaseIdle() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      // Fade out tension shake sound if it's playing
      soundManager.fadeOut(SOUND_NAME.tension_shake, 500);

      this.backgroundSpine.state.clearTracks();
      this.backgroundSpine.state.setAnimation(0, "base_idle", true);
    } catch (error) {
      console.warn(
        "[BackgroundAnimationController] Error playing base_idle:",
        error
      );
    }
  }

  // Play feature_idle animation (for Hold and Win feature)
  playFeatureIdle() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      // Fade out tension shake sound if it's playing
      soundManager.fadeOut(SOUND_NAME.tension_shake, 500);

      this.backgroundSpine.state.clearTracks();
      this.backgroundSpine.state.setAnimation(0, "feature_idle", true);
    } catch (error) {
      // Silent fail
    }
  }

  // Play base_event intro animation on track 1 (tension start)
  // Track 0 continues playing base_idle/feature_idle without interruption
  playBaseEventIn() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      // Start looping tension shake sound
      soundManager.play(SOUND_NAME.tension_shake);

      const animations = this.getAvailableAnimations();

      // Play on track 1 (track 0 keeps playing idle animation)
      // Play intro animation, then queue looping animation
      if (animations.includes("base_event_in") && animations.includes("base_event_idle")) {
        this.backgroundSpine.state.setAnimation(1, "base_event_in", false);
        this.backgroundSpine.state.addAnimation(1, "base_event_idle", true, 0);
      } else if (animations.includes("base_event_idle")) {
        // Fallback: just play loop if intro not available
        this.backgroundSpine.state.setAnimation(1, "base_event_idle", true);
      }
    } catch (error) {
      console.warn(
        "[BackgroundAnimationController] Error playing base_event_in:",
        error
      );
    }
  }

  // Play base_event exit animation on track 1 (tension end)
  // Track 0 continues playing base_idle/feature_idle without interruption
  playBaseEventOut() {
    if (!this.backgroundSpine || !this.backgroundSpine.state) {
      return;
    }

    try {
      // Fade out tension shake sound
      soundManager.fadeOut(SOUND_NAME.tension_shake, 500);

      const currentTrack = this.backgroundSpine.state.tracks[1];
      const currentAnimation = currentTrack?.animation?.name;

      // Check if we're currently playing base_event animations on track 1
      const isPlayingBaseEvent =
        currentAnimation === "base_event_in" ||
        currentAnimation === "base_event_idle";

      if (!isPlayingBaseEvent) {
        return;
      }

      const animations = this.getAvailableAnimations();

      // Play exit animation on track 1 if available
      if (animations.includes("base_event_out")) {
        this.backgroundSpine.state.setAnimation(1, "base_event_out", false);
        // Track 1 will be empty after exit animation completes (track 0 continues idle)
      } else {
        // Fallback: just clear track 1 if exit animation not available
        this.backgroundSpine.state.clearTrack(1);
      }
    } catch (error) {
      console.warn(
        "[BackgroundAnimationController] Error playing base_event_out:",
        error
      );
    }
  }

  /**
   * Update Y offset based on intro popup state
   */
  updateYOffset() {
    this.yOffset = this.introPopupActive
      ? BACKGROUND_ANIMATION_CONFIG.yOffsetIntro
      : BACKGROUND_ANIMATION_CONFIG.yOffset;
  }

  /**
   * Check if offset should be applied based on device and orientation
   * Only apply offset on desktop or landscape orientation
   */
  shouldApplyOffset() {
    // Desktop always gets offset
    if (this.device === PixiSettings.DEVICE.DESKTOP) {
      return true;
    }

    // Mobile/Tablet in landscape gets offset
    if (this.orientation === PixiSettings.ORIENTATION.LANDSCAPE) {
      return true;
    }

    // Portrait mobile/tablet: no offset
    return false;
  }

  // Apply responsive scaling and positioning to maintain stone ledge alignment
  applyResponsiveBackgroundScaling() {
    if (!this.dimensions) return;

    const { width, height } = this.dimensions;

    // Only scale background when symbols start shrinking (scaleFactor < 1)
    const backgroundScale = Math.min(1, this.symbolScale);

    // Use width directly - it's already capped at MAX_WIDTH in DimensionsContext
    const centerX = width / 2;

    // Calculate separate offsets for sprite and spine based on their respective scales
    // Sprite uses backgroundScale (capped at 1), spine uses symbolScale (can be > 1)
    let spriteScaledYOffset = 0;
    let spineScaledYOffset = 0;
    if (this.shouldApplyOffset()) {
      // Stone ledge offset - scale it proportionally to each element's actual scale
      spriteScaledYOffset = this.yOffset * backgroundScale;
      spineScaledYOffset = this.yOffset * this.symbolScale;
    }
    const spritePositionY = height / 2 + spriteScaledYOffset;

    // Add spine-specific Y offset (always applied, scaled to symbolScale)
    const spineAdditionalYOffset = BACKGROUND_ANIMATION_CONFIG.yOffsetSpine * this.symbolScale;
    const spinePositionY = height / 2 + spineScaledYOffset + spineAdditionalYOffset;

    // Calculate X position for spine (with optional horizontal offset)
    // Spine gets additional X offset, scaled proportionally to symbolScale
    const spineScaledXOffset = BACKGROUND_ANIMATION_CONFIG.xOffset * this.symbolScale;
    const spinePositionX = centerX + spineScaledXOffset;

    // Apply scaling and positioning to JPG sprite
    if (this.backgroundSprite) {
      const sprite = this.backgroundSprite.current || this.backgroundSprite;
      if (sprite && sprite.scale && sprite.position) {
        sprite.scale.set(backgroundScale);
        sprite.position.set(centerX, spritePositionY);
      }
    }

    // Apply scaling and positioning to spine animation (acts independently)
    if (this.backgroundSpine) {
      this.backgroundSpine.scale.set(this.symbolScale);
      this.backgroundSpine.position.set(spinePositionX, spinePositionY);
    }
  }

  // Update dimensions and reapply scaling
  resizeBackground(
    dimensions,
    symbolWidth,
    symbolScale,
    device,
    orientation,
    introPopupActive = false
  ) {
    this.dimensions = dimensions;
    this.symbolWidth = symbolWidth;
    this.symbolScale = symbolScale;
    this.device = device;
    this.orientation = orientation;
    this.introPopupActive = introPopupActive;
    this.updateYOffset();
    this.applyResponsiveBackgroundScaling();
  }

  // Get list of available animation names
  getAvailableAnimations() {
    if (!this.backgroundSpine?.skeleton?.data?.animations) {
      return [];
    }
    return this.backgroundSpine.skeleton.data.animations.map(
      (anim) => anim.name
    );
  }
}

export default BackgroundAnimationController;
